import {type PropsWithChildren, useMemo} from 'react'
import styled, {css} from 'styled-components'

import type {Person} from '@pleo-io/deimos'
import {Avatar, type AvatarProps, tokens} from '@pleo-io/telescope'
import {Wallet} from '@pleo-io/telescope-icons'
import {__alphaTokens} from '@pleo-io/telescope-tokens'

import type {UserData} from '@product-web/user'

import deactivatedIcon from './images/deactivated.svg'

export const getName = (employee?: Partial<Person>) => {
    if (!employee) {
        return '?'
    }

    const initials = [(employee.firstName || '')[0], (employee.lastName || '')[0]]
        .join('')
        .toUpperCase()

    if (initials) {
        return `${employee?.firstName || ''} ${employee?.lastName || ''}`
    }

    return employee.email || '?'
}

export type AvatarStatus = 'invited' | 'deactivated' | 'active'
export type AvatarWithStatusProps = AvatarProps & {status?: AvatarStatus}
export const AvatarWithStatus = ({
    status = 'active',
    children,
    ...props
}: PropsWithChildren<AvatarWithStatusProps>) => {
    const isInvited = status === 'invited'
    const isDeactivated = status === 'deactivated'

    const icon = isInvited ? <InvitedAvatar /> : props.icon

    return (
        <StyledAvatar $deactivated={isDeactivated} {...props} icon={icon}>
            {isDeactivated ? (
                <Avatar.Badge icon={<img src={deactivatedIcon} alt="" />} />
            ) : (
                children
            )}
        </StyledAvatar>
    )
}

type AvatarEmployeeProps = AvatarWithStatusProps &
    Partial<Pick<UserData, 'role'>> & {
        employee?: Partial<Person>
    }

export const AvatarEmployee = ({
    employee,
    role,
    status = 'active',
    children,
    ...props
}: PropsWithChildren<AvatarEmployeeProps>) => {
    const isBookkeeper = useMemo(() => role && role.startsWith('bookkeeper'), [role])

    const icon = isBookkeeper ? <Wallet /> : undefined

    return (
        <AvatarWithStatus
            status={status}
            name={getName(employee)}
            src={employee?.avatar?.url}
            icon={icon}
            {...props}
        >
            {children}
        </AvatarWithStatus>
    )
}

const InvitedAvatar = styled.div`
    position: relative;
    display: inline-flex;
    cursor: default;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: ${__alphaTokens.colorContentStatic};
    font-size: ${tokens.fontLarge};
    background-color: ${tokens.shade000};
    border: 1px dashed ${tokens.shade400};
    border-radius: ${tokens.arc99999};
`

const StyledAvatar = styled(Avatar)<{$deactivated?: boolean}>`
    ${({$deactivated}) =>
        $deactivated &&
        css`
            background-color: ${tokens.shade300};
        `}

    & > img {
        ${({$deactivated}) =>
            $deactivated &&
            css`
                opacity: ${tokens.opacity40};
            `}
    }
`
